<script setup lang="ts">
import {
    useDailyActivitiesStore,
    Independent,
    NeedsHelp,
    Dependant,
    NonPermanentWheelchair,
    PermanentWheelchair,
    DailyNursing,
    Bedridden,
    MinorHelp,
    MajorHelp,
    Unable,
    Continent,
    OccasionalAccident,
    Incontinent,
    Stable,
    Deteriorating,
    RapidDeterioration,
    DailyActivitiesLabels,
} from "@/stores/dailyActivities";
import Question from "./fields/Question.vue";
import Select from "./fields/Select.vue";
import { tooltips } from "../tooltips/dailyActivitiesTooltips";
import { dailyActivitiesValidator as validator } from "../utils/validators";

const store = useDailyActivitiesStore();

</script>
<template>
    <div class="row">
        <div class="alert alert-info text-center zInd2">
            <p>
                Please select the answers that most closely match your current physical capabilities.
            </p>
            <p>
                Some of these questions may not apply to you. But annuity providers will use your answers to understand your health. They will also look at how the conditions you have disclosed may be affecting your day-to-day life.
            </p>
        </div>
    </div>
    <Question
        name="dressYourself"
        label="How would you describe your ability to dress yourself?"
        :tooltip="tooltips.dressYourself"
        :include-padding="false"
    >
        <Select
            v-model="store.dressYourself"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.dressYourself[Independent], value: Independent},
                {label: DailyActivitiesLabels.dressYourself[NeedsHelp], value: NeedsHelp},
                {label: DailyActivitiesLabels.dressYourself[Dependant], value: Dependant},
            ]"
            :validation="validator.dressYourself"
        />
    </Question>
    <Question
        name="applicantMobility"
        label="How easily can you move from one place to another?"
        :tooltip="tooltips.applicantMobility"
        :include-padding="false"
    >
        <Select
            v-model="store.applicantMobility"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantMobility[Independent], value: Independent},
                {label: DailyActivitiesLabels.applicantMobility[NeedsHelp], value: NeedsHelp},
                {label: DailyActivitiesLabels.applicantMobility[NonPermanentWheelchair], value: NonPermanentWheelchair},
                {label: DailyActivitiesLabels.applicantMobility[PermanentWheelchair], value: PermanentWheelchair},
                {label: DailyActivitiesLabels.applicantMobility[DailyNursing], value: DailyNursing},
                {label: DailyActivitiesLabels.applicantMobility[Bedridden], value: Bedridden},
            ]"
            :validation="validator.applicantMobility"
        />
    </Question>
    <Question
        name="applicantTransferring"
        label="How well are you able to move from one position to another? e.g. from a sitting to a standing position"
        :tooltip="tooltips.applicantTransferring"
        :include-padding="false"
    >
        <Select
            v-model="store.applicantTransferring"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantTransferring[Independent], value: Independent},
                {label: DailyActivitiesLabels.applicantTransferring[MinorHelp], value: MinorHelp},
                {label: DailyActivitiesLabels.applicantTransferring[MajorHelp], value: MajorHelp},
                {label: DailyActivitiesLabels.applicantTransferring[Unable], value: Unable},
            ]"
            :validation="validator.applicantTransferring"
        />
    </Question>
    <Question
        name="applicantBladder"
        label="How would you describe your bladder control?"
        :tooltip="tooltips.applicantBladder"
        :include-padding="false"
    >
        <Select
            v-model="store.applicantBladder"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantBladder[Continent], value: Continent},
                {label: DailyActivitiesLabels.applicantBladder[OccasionalAccident], value: OccasionalAccident},
                {label: DailyActivitiesLabels.applicantBladder[Incontinent], value: Incontinent},
            ]"
            :validation="validator.applicantBladder"
        />
    </Question>
    <Question
        name="applicantBowels"
        label="How would you describe your bowel control?"
        :tooltip="tooltips.applicantBowels"
        :include-padding="false"
    >
        <Select
            v-model="store.applicantBowels"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantBowels[Continent], value: Continent},
                {label: DailyActivitiesLabels.applicantBowels[OccasionalAccident], value: OccasionalAccident},
                {label: DailyActivitiesLabels.applicantBowels[Incontinent], value: Incontinent},
            ]"
            :validation="validator.applicantBowels"
        />
    </Question>
    <Question
        name="applicantBathing"
        label="How would you describe your ability to bathe yourself and get in and out of the bath?"
        :tooltip="tooltips.applicantBathing"
        :include-padding="false"
    >
        <Select
            v-model="store.applicantBathing"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantBathing[Independent], value: Independent},
                {label: DailyActivitiesLabels.applicantBathing[NeedsHelp], value: NeedsHelp},
                {label: DailyActivitiesLabels.applicantBathing[Dependant], value: Dependant},
            ]"
            :validation="validator.applicantBathing"
        />
    </Question>
    <Question
        name="applicantFeeding"
        label="How would you describe your ability to feed yourself including both whole and blended foods?"
        :tooltip="tooltips.applicantFeeding"
        :include-padding="false"
    >
        <Select
            v-model="store.applicantFeeding"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantFeeding[Independent], value: Independent},
                {label: DailyActivitiesLabels.applicantFeeding[NeedsHelp], value: NeedsHelp},
                {label: DailyActivitiesLabels.applicantFeeding[Unable], value: Unable},
            ]"
            :validation="validator.applicantFeeding"
        />
    </Question>
    <Question
        name="applicantProgression"
        label="Has there been any progression in any of the above activities in the last 5 years?"
        :tooltip="tooltips.applicantProgression"
        :include-padding="true"
    >
        <Select
            v-model="store.applicantProgression"
            placeholder="Please Select"
            :options="[
                {label: DailyActivitiesLabels.applicantProgression[Stable], value: Stable},
                {label: DailyActivitiesLabels.applicantProgression[Deteriorating], value: Deteriorating},
                {label: DailyActivitiesLabels.applicantProgression[RapidDeterioration], value: RapidDeterioration},
            ]"
            :validation="validator.applicantProgression"
        />
    </Question>
</template>
